import { getCsrfTokenApi, logoutApi, refreshTokenApi } from '~/api/auth';
import { useAuthRuntime } from '~/composables/useAuthRuntime';
import { useCookieOptions } from '~/composables/useCookieOptions';
import { useDeviceId } from '~/composables/useDeviceId';
import { useResetStore } from '~/composables/useResetStore';
import { STORAGE_KEYS } from '~/constants/storageKeys';
import { useAuthStore } from '~/stores/auth';
import { useProfileStore } from '~/stores/profile';
import { useRoleStore } from '~/stores/role';
import { checkJwtOrgScope } from '~/utils/jwt';
import { navigateTo } from '#app';

interface AuthHeaders {
  [key: string]: string;
  TOKEN: string;
}

/**
 * 是否已經有人觸發強制登出。
 *
 * 需要共享是因為多支 API 可能同時收到 401（例如開機時 permissions plugin 會並行打
 * /role/member/permissions 與 /role/apis），每一支都會各自走一次登出流程，結果是重複
 * logout + 重複整頁跳轉互相打架。第一個進來的建立 promise，其餘一律共用（見
 * `forceLogoutToLogin`），確保整個頁面生命週期只跳轉一次、只跳一次 toast。
 *
 * 這個 promise 刻意不在完成後重設為 null：跳轉會整頁重載，狀態自然消失。但「重載會清掉
 * 狀態」只在瀏覽器端成立，所以它存放在 `useAuthRuntime()` 而不是模組層 ——
 * 伺服器端的模組層是整個 process、所有使用者共用的，詳見該檔案的說明。
 */
export const isForcingLogout = (): boolean => useAuthRuntime().forceLogoutPromise !== null;

/**
 * auth state 的世代編號。
 *
 * 換 token 是「送出 → 等回應 → 寫入」三段，中間隔著一次網路往返。這期間如果有人做了
 * 更權威的事（切換組織、登入、登出），先前那支換回來的就是**舊 session** 的 token，
 * 寫進去會把新的蓋掉 —— 畫面是新組織、API 卻用舊組織 scope，跨組織讀到別人的資料。
 * 「各自送出」只解決了請求端，沒解決寫入端：晚回來的那支照樣會蓋。
 *
 * 規則：每支 refresh 出發前記下當時的世代，回來要寫入之前先確認自己仍是當前世代，
 * 不是就把結果丟掉。切換組織／登入／登出會遞增世代，一次讓所有在途的舊請求失效。
 */
const currentAuthGeneration = (): number => useAuthRuntime().authGeneration;

const bumpAuthGeneration = (): number => {
  const runtime = useAuthRuntime();
  runtime.authGeneration += 1;
  return runtime.authGeneration;
};

const isCurrentAuthGeneration = (generation: number): boolean =>
  generation === currentAuthGeneration();

/**
 * 進行中的換 token 請求，同一時間只允許一個。
 *
 * refresh token 是一次性的：換過就作廢。而「同時有多支請求 401」是常態
 * （開機時 permissions plugin 與路由守衛的組織檢查會並行），如果每一支各自去 refresh，
 * 第一支換成功後把 token 換掉，第二支手上那張已經被消費，後端回 422「刷新令牌已過期」，
 * 於是明明 session 還活著卻被判定過期、整頁踢回登入頁。
 *
 * 這裡讓所有 refresh 路徑（API 401 自動續期、路由守衛、切換組織）共用同一個請求。
 *
 * 但**只有拿著同一張 refresh token 的人才能共用**。共用的前提是「大家想換的是同一個東西」，
 * 換的來源不同就不成立：切換組織時 change API 會先發一張新的 refresh token，此時舊頁面
 * 殘留的請求可能正拿著**舊**的那張在 refresh，切組織的流程若共用它，拿回來的 token 是舊
 * session 換出來的 —— scope 還是舊組織，卻會被當成「切換成功」寫進 cookie，
 * 整頁重載後每支 API 都讀到上一個組織的資料。
 */
const refreshTokensOnce = (refreshToken: string, options?: { force?: boolean }) => {
  const runtime = useAuthRuntime();

  if (!options?.force && runtime.tokenRefreshPromise && runtime.tokenRefreshKey === refreshToken) {
    return runtime.tokenRefreshPromise;
  }

  const promise = refreshTokenApi(refreshToken).finally(() => {
    // 只清掉自己這一支。force 另開時舊的那支可能比較晚結束，
    // 沒有這個判斷就會把後來者的 promise 一起清掉，讓下一個呼叫又多送一次 refresh。
    if (runtime.tokenRefreshPromise === promise) {
      runtime.tokenRefreshPromise = null;
      runtime.tokenRefreshKey = '';
    }
  });

  runtime.tokenRefreshPromise = promise;
  runtime.tokenRefreshKey = refreshToken;

  return promise;
};

/**
 * 請同源的 server route 清掉瀏覽器裡剩下的認證 cookie。
 *
 * Google 登入的 Authorization / RefreshToken / memberinfo 是後端寫在父網域上的 HttpOnly
 * cookie，`document.cookie` 刪不動（瀏覽器會忽略 JS 對 HttpOnly cookie 的覆寫），
 * 只有 HTTP 回應帶的 Set-Cookie 能刪。沒清掉的話，登出後整頁跳轉到 /login 時 SSR 仍會
 * 從 request header 讀到它們、判定已登入，把人導回 dashboard。
 *
 * 失敗不阻擋登出：本地狀態已經清掉了，最差的情況是回到原本的行為。
 *
 * 路徑刻意不放在 /api/ 底下：專案根目錄的 api/ 資料夾會讓 Vercel 接管 /api/*，
 * 對不到檔案就回 404，Nitro 的路由收不到（詳見 server/routes/_auth/logout.post.ts）。
 */
const clearServerSideAuthCookies = async (): Promise<void> => {
  if (!import.meta.client) return;

  try {
    await $fetch('/_auth/logout', { method: 'POST' });
  } catch (error) {
    console.warn('Failed to clear server-side auth cookies:', error);
  }
};

const AUTH_PAGE_PATTERN = /\/(login|signup|forgot-password|auth\/callback)(\/|$)/;

/** 目前是否已經在認證相關頁面（此時不需要再導向，避免不必要的整頁重載） */
const isOnAuthPage = (): boolean => {
  if (!import.meta.client) return false;
  return AUTH_PAGE_PATTERN.test(window.location.pathname.toLowerCase());
};

export const useAuth = () => {
  const authStore = useAuthStore();
  const localePath = useLocalePath();

  const getAuthHeaders = (): AuthHeaders => {
    return {
      TOKEN: authStore.getToken() || '',
    };
  };

  /**
   * 靜默刷新 token（用於 API 請求中的自動刷新）
   * 只更新 token，不觸發登入流程的重定向
   * @returns {Promise<boolean>} 刷新是否成功
   */
  const refreshTokenSilently = async (): Promise<boolean> => {
    // 伺服器端一律不換（見 refreshTokenOnUnauthorized 的說明）。
    // 回 false 不會把人踢出去：useApiFetch 的 401 流程在伺服器端也不走登出。
    if (import.meta.server) {
      return false;
    }

    // getRefreshToken() 已過期時會回空字串，這裡直接短路，
    // 不要再送出一個註定拿到 422「Refresh token has expired」的請求
    const currentRefreshToken = authStore.getRefreshToken();
    if (!currentRefreshToken) {
      return false;
    }

    const generation = currentAuthGeneration();

    try {
      const data = await refreshTokensOnce(currentRefreshToken);

      if (!isCurrentAuthGeneration(generation)) {
        // 這支在途期間有人接手了（多半是切換組織），換回來的是舊 session 的 token，不能寫。
        // 回 true 而不是 false：session 本身好好的，只是這支的結果作廢而已。
        // 回 false 會讓 useApiFetch 的 401 流程判定「登入過期」把使用者踢去登入頁。
        console.warn('Discarding refreshed token from a superseded auth generation');
        return true;
      }

      // 只更新 token，不觸發登入流程
      authStore.setTokens(data.token, data.refresh_token);
      const csrfSuccess = await refreshCsrfTokenSilently(generation);
      if (!csrfSuccess) {
        console.warn('CSRF token refresh failed after silent token refresh');
      }
      return true;
    } catch (error) {
      console.warn('Silent token refresh failed:', error);
      return false;
    }
  };

  /**
   * @param generation 呼叫端出發時的 auth 世代。CSRF token 是綁 session 的，
   *                   世代已經被推進代表這支換回來的屬於舊 session，寫進去會讓
   *                   當前 session 帶著別人的 CSRF token，之後每支寫入 API 都 403。
   */
  const refreshCsrfTokenSilently = async (generation?: number): Promise<boolean> => {
    const expectedGeneration = generation ?? currentAuthGeneration();
    try {
      const csrfToken = await getCsrfTokenApi();
      if (!csrfToken) {
        return false;
      }
      if (!isCurrentAuthGeneration(expectedGeneration)) {
        console.warn('Discarding CSRF token from a superseded auth generation');
        return false;
      }
      authStore.setCsrfToken(csrfToken);
      return true;
    } catch (error) {
      console.warn('Silent CSRF token refresh failed:', error);
      return false;
    }
  };

  /**
   * 清除本地登入狀態並導向登入頁。
   *
   * 所有「session 已死，救不回來了」的路徑都應該走這裡，不要各自呼叫 logout + 跳轉。
   * 併發呼叫會共用同一個 promise，只會實際執行一次。
   */
  const forceLogoutToLogin = async (): Promise<void> => {
    // 伺服器端一律不做。
    //
    // logout() 會呼叫 authStore.clearTokens()，而 SSR 的 useCookie 寫入會在 app:rendered
    // 時變成刪除用的 Set-Cookie —— 等於由伺服器把瀏覽器手上還有效的 token 清掉。
    // 但「SSR 取不到組織資訊」不等於使用者真的登出了（後端抖一下、cookie 還沒遷移完都會
    // 走到這裡），這一刀砍下去反而製造出真正的登出。
    // 真的失效時，瀏覽器端 hydrate 之後會自己走一次同樣的流程並顯示「登入已過期」。
    if (import.meta.server) {
      return;
    }

    const runtime = useAuthRuntime();
    if (runtime.forceLogoutPromise) {
      return runtime.forceLogoutPromise;
    }

    runtime.forceLogoutPromise = (async () => {
      try {
        // 跳過登出 API：token 已失效，呼叫 logoutApi 只會再換來一個 401
        await logout(undefined, true);
      } catch (error) {
        console.warn('Logout failed while forcing re-authentication:', error);
      }

      // Nuxt 的 useCookie 是用 watcher（flush: 'pre'）寫入 document.cookie 的，
      // 等它 flush 完再跳轉，否則整頁重載後 cookie 可能還沒被真正刪掉，
      // 下一次載入又會誤判成已登入，形成跳轉迴圈。
      await nextTick();

      if (!import.meta.client || isOnAuthPage()) {
        return;
      }
      window.location.href = localePath('/login');
    })();

    return runtime.forceLogoutPromise;
  };

  const refreshTokenOnUnauthorized = async () => {
    // 伺服器端一律不換 token。
    //
    // Nuxt 的 useCookie 在伺服器端是掛在 app:rendered 才寫 Set-Cookie 的，middleware 只要
    // 以 navigateTo 收尾（302）就不會 render，換到的新 token **送不回瀏覽器**——但後端已經
    // 把那張一次性的 refresh token 消費掉了。結果是瀏覽器手上留著一張作廢的 refresh token，
    // 下一次續期必定 422，使用者莫名其妙被踢回登入頁。
    // 交給瀏覽器端：hydrate 之後 useApiFetch 的 401 流程本來就會自己 refresh 並重試。
    if (import.meta.server) {
      return;
    }

    // getRefreshToken() 會擋掉已過期的 refresh token，避免拿去換一個必定失敗的結果
    const currentRefreshToken = authStore.getRefreshToken();
    if (!currentRefreshToken) {
      await forceLogoutToLogin();
      return;
    }

    const generation = currentAuthGeneration();

    try {
      const data = await refreshTokensOnce(currentRefreshToken);
      if (!isCurrentAuthGeneration(generation)) {
        // 有人接手了（切換組織／登入／登出），這支換回來的屬於舊 session，直接丟掉。
        // 不走 forceLogoutToLogin：session 沒死，把人踢出去才是錯的。
        console.warn('Discarding refreshed token from a superseded auth generation');
        return;
      }
      await login(data.token, data.refresh_token);
    } catch (error) {
      // refreshToken 也失效，清除本地資料並跳轉
      await forceLogoutToLogin();
    }
  };

  /**
   * 切換／建立／離開組織之後把 token 換成帶新組織 scope 的那一張。
   *
   * @param expectedOrgUuid 預期切到的組織。有給就會驗證換回來的 token 真的是這個組織的；
   *                        離開組織這種「後端自己挑下一個」的情境無從預期，不給即可。
   */
  const refreshTokenAfterOrgSwitch = async (expectedOrgUuid?: string) => {
    // 切組織一律是使用者在瀏覽器端的動作；伺服器端走到這裡代表有東西接錯了。
    // 照樣短路，理由與 refreshTokenOnUnauthorized 相同（換到的 token 送不回瀏覽器）。
    if (import.meta.server) {
      return false;
    }

    // 用 getRefreshToken()（會擋掉過期的那張）而不是 state 上的 refreshToken：
    // 只存在 cookie、還沒同步進 store 的情況下，讀 state 會誤判成「沒有 refresh token」
    const currentRefreshToken = authStore.getRefreshToken();
    if (!currentRefreshToken) {
      console.warn('No refresh token available for organization switch');
      return false;
    }

    // 切組織是最權威的一次 auth 變更：先把世代推進，讓所有在途的舊 refresh 一律作廢。
    // 少了這一步，舊 session 的 silent refresh 只要晚一步完成，就會把新組織的 token
    // 蓋回舊的 —— 送出端各自送出解決不了寫入端的覆寫。
    const generation = bumpAuthGeneration();

    try {
      // force：一定要用「change API 剛回來的那張 refresh token」重換一次，不共用進行中的那支。
      // 詳見 refreshTokensOnce 的說明——共用會換到舊組織 scope 的 token。
      const data = await refreshTokensOnce(currentRefreshToken, { force: true });

      if (!isCurrentAuthGeneration(generation)) {
        // 有更新的一次組織切換／登入接手了，讓它去寫，這支安靜退場。
        console.warn('Organization switch superseded by a newer auth change');
        return true;
      }

      // 「拿到 token」不等於「拿到新組織的 scope」。這裡是唯一還能攔下來的地方：
      // 一旦把錯 scope 的 token 寫進 cookie，整頁重載之後畫面是新組織、資料全是舊組織的，
      // 而且看起來一切正常，沒有任何錯誤可以追。
      // checkJwtOrgScope 認不出組織欄位時回 unknown，維持原本「有 token 就放行」的行為。
      if (expectedOrgUuid && checkJwtOrgScope(data.token, expectedOrgUuid) === 'mismatch') {
        console.warn(
          'Refreshed token still carries a different organization scope; treating org switch as failed'
        );
        return false;
      }

      authStore.clearTokens();
      await nextTick();
      authStore.setTokens(data.token, data.refresh_token);
      const csrfSuccess = await refreshCsrfTokenSilently(generation);
      if (!csrfSuccess) {
        console.warn('CSRF token refresh failed after organization switch');
      }
      return true;
    } catch (error) {
      console.warn('Failed to refresh token after organization switch:', error);
      return false;
    }
  };

  const getRedirectUrl = (): { path: string; query: Record<string, string> } | null => {
    // 檢查是否有需要重定向的 URL（優先 cookie，再 localStorage）
    let redirectDataObj: { path: string; query: Record<string, string> } | null = null;

    if (!import.meta.client) {
      return null;
    }

    // 先嘗試讀 cookie
    try {
      // 使用統一的 cookie 選項，確保 domain 設定正確
      const redirectCookie = useCookie(
        STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
        useCookieOptions<string>()
      );
      const cookieVal = redirectCookie.value;
      if (cookieVal) {
        if (typeof cookieVal === 'string') {
          try {
            redirectDataObj = JSON.parse(cookieVal);
          } catch {
            // 如果不是有效 JSON，忽略，改用 localStorage
          }
        } else if (typeof cookieVal === 'object') {
          redirectDataObj = cookieVal;
        }
      }
    } catch {
      // ignore
    }

    // 如果 cookie 沒有或解析失敗，再用 localStorage（字串）
    if (!redirectDataObj) {
      const redirectJson = localStorage.getItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      if (redirectJson) {
        try {
          redirectDataObj = JSON.parse(redirectJson);
        } catch {
          // ignore
        }
      }
    }

    return redirectDataObj;
  };

  /**
   * 記住登入前想去的位置，登入成功後回到那裡。
   * cookie 與 localStorage 並存：cookie 撐得過整頁跳轉，localStorage 作為回退。
   */
  const saveRedirectUrl = (path: string, query: Record<string, unknown>): void => {
    const json = JSON.stringify({ path, query });
    try {
      useCookie<string>(
        STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
        useCookieOptions({
          sameSite: 'lax',
          maxAge: 60 * 10, // 10 分鐘
        })
      ).value = json;
    } catch {
      // cookie 寫入失敗（例如 SSR context 不可用）時仍保留 localStorage 這條路
    }
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, json);
    }
  };

  const clearRedirectUrl = (): void => {
    useCookie(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, useCookieOptions<string>()).value = '';
    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
    }
  };

  /**
   * 消費 redirect_after_login：有就回到原本要去的位置，沒有就去 dashboard。
   * 登入成功後（useAuth.login）與 middleware 發現已登入卻停在登入頁時，走的都是這一份。
   */
  const consumeRedirectUrl = (): { path: string; query: Record<string, string> } | null => {
    const redirectDataObj = getRedirectUrl();
    if (!redirectDataObj?.path) {
      return null;
    }
    clearRedirectUrl();
    return redirectDataObj;
  };

  const handleRedirectAfterLogin = async (): Promise<void> => {
    const target = consumeRedirectUrl();
    if (target) {
      await navigateTo({ path: target.path, query: target.query });
      return;
    }

    // 如果沒有重定向 URL，則重定向到 dashboard
    await navigateTo(localePath('/dashboard'));
  };

  const login = async (_token: string, _refreshToken: string | null, redirect_uri?: string) => {
    // 如果沒有提供 refresh_token，嘗試從 store 中取得
    const finalRefreshToken = _refreshToken || authStore.getRefreshToken();

    // 登入是權威的 auth 變更：作廢所有在途的舊 refresh，免得它們晚一步把新 session 蓋掉
    const generation = bumpAuthGeneration();

    authStore.setTokens(_token, finalRefreshToken);
    await nextTick();
    const csrfSuccess = await refreshCsrfTokenSilently(generation);
    if (!csrfSuccess) {
      console.warn('CSRF token refresh failed after login');
    }

    // 登入成功後自動載入權限資料
    try {
      const roleStore = useRoleStore();
      await Promise.all([
        roleStore.getMemberRules(),
        // roleStore.getApiGroups()
        roleStore.getAllUserAuthApis(),
      ]);
    } catch (error) {
      console.warn('load permission data failed:', error);
      // 不阻擋登入流程，讓組件可以fallback到原本的載入邏輯
    }

    if (redirect_uri) {
      clearRedirectUrl();
      await navigateTo(redirect_uri);
      return;
    }

    // 處理登入後的重定向邏輯
    await handleRedirectAfterLogin();
  };

  /**
   * 登出功能
   * @param deviceId 可選的設備 ID，如果提供則只登出該設備，否則登出所有設備
   * @param skipApi 是否跳過 API 調用（用於 token 失效時避免無限循環）
   */
  const logout = async (deviceId?: string, skipApi = false) => {
    // 先作廢所有在途的 refresh：清完之後才回來的那支會把 token 寫回去，
    // 使用者已經登出了卻又變成登入狀態
    bumpAuthGeneration();

    // 如果沒有提供 deviceId，嘗試從 useDeviceId 獲取
    let finalDeviceId = deviceId;
    if (!finalDeviceId && import.meta.client) {
      try {
        const { getOrCreateDeviceId } = useDeviceId();
        finalDeviceId = getOrCreateDeviceId();
      } catch (error) {
        console.warn('Failed to get device ID for logout:', error);
      }
    }

    // 只有在不跳過 API 時才呼叫登出 API
    // 當 token 失效時，跳過 API 調用避免無限循環（因為 logoutApi 需要認證）
    if (!skipApi) {
      try {
        await logoutApi(finalDeviceId);
      } catch (error) {
        // 登出 API 失敗時記錄錯誤，但不阻擋本地清除流程
        // 這樣即使 API 失敗，用戶也能正常登出
        console.warn('Logout API call failed, but continuing with local cleanup:', error);
      }
    } else {
      console.log('Skipping logout API call due to token expiration');
    }

    // 使用 useResetStore 重置所有 stores
    const resetStore = useResetStore();
    resetStore.all();

    // 清除 cookies（因為 pinia reset 不會清除 cookies）
    authStore.clearTokens();

    // clearTokens 只清得掉前端自己寫的 host-only cookie；後端在 Google 登入時寫在
    // 父網域上的 HttpOnly cookie 得靠 server route 回 Set-Cookie 才刪得掉。
    // 放在這裡（而不是 logoutAndRedirect）是為了讓強制登出（forceLogoutToLogin）
    // 也走到，否則 401 被踢出去之後一樣會被那份殘留 cookie 判定成已登入。
    await clearServerSideAuthCookies();
  };

  /**
   * 使用者主動登出：呼叫登出 API、清除本地狀態，然後回到登入頁。
   *
   * 與 forceLogoutToLogin 的差別在於這是有意識的操作，會實際呼叫 logoutApi；
   * forceLogoutToLogin 是 session 已死時的被動出口，會跳過 API。
   */
  const logoutAndRedirect = async (deviceId?: string): Promise<void> => {
    await logout(deviceId);

    // 等 useCookie 的 watcher 把清除寫進 document.cookie 再跳轉
    await nextTick();

    if (!import.meta.client) {
      await navigateTo(localePath('/login'));
      return;
    }

    // 整頁重載而非 SPA 導航：pinia、模組層變數、各種 polling 一次全部歸零，
    // 不必依賴每個 store 都有把自己清乾淨（與 forceLogoutToLogin 的行為一致）
    window.location.href = localePath('/login');
  };

  /**
   * 處理 Google 社交登入
   * 在導向 Google 授權頁面之前，先清除前端之前設定的 cookies
   */
  const handleSocialLogin = (invite_code?: string) => {
    localStorage.setItem('from_url', window.location.href);
    const config = useRuntimeConfig();
    const clientId = config.public.GOOGLE_CLIENT_ID as string;
    const redirectUri = config.public.GOOGLE_REDIRECT_URI as string;

    // 在導向 Google 授權頁面之前，先清除前端之前設定的 cookies
    if (import.meta.client) {
      authStore.clearTokens();
    }

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    // 統一導向 callback 頁面，將 invite_code 等參數帶在 state 中
    let stateUrl = `${window.location.origin}/auth/callback`;
    if (invite_code) {
      stateUrl += `?invite_code=${encodeURIComponent(invite_code)}`;
    }

    const params = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: stateUrl,
    };

    for (const [key, value] of Object.entries(params)) {
      authUrl.searchParams.append(key, value);
    }

    window.location.href = authUrl.toString();
  };

  return {
    login,
    logout,
    logoutAndRedirect,
    forceLogoutToLogin,
    refreshTokenOnUnauthorized,
    refreshTokenSilently,
    refreshCsrfTokenSilently,
    refreshTokenAfterOrgSwitch,
    getAuthHeaders,
    handleSocialLogin,
    getRedirectUrl,
    saveRedirectUrl,
    clearRedirectUrl,
    consumeRedirectUrl,
    handleRedirectAfterLogin,
  };
};
